/**
 * Navigation.js
 *
 * Handles mobile hamburger menu:
 * - Burger button toggles open/close (no separate close button)
 * - Burger SVG animates to × via CSS (aria-expanded drives the animation)
 * - Full-screen overlay slides down from top
 * - aria-expanded state management
 * - Focus trap inside overlay
 * - Escape key to close
 * - Body scroll lock
 */

export default class Navigation {
	constructor() {
		this.burger   = document.querySelector( '.js-nav-burger' );
		this.overlay  = document.querySelector( '.js-nav-overlay' );
		this.header   = document.querySelector( '.js-header' );
		this.isOpen   = false;

		if ( ! this.burger || ! this.overlay ) return;

		this._bindEvents();
		this._bindScroll();
	}

	_bindScroll() {
		if ( ! this.header ) return;
		window.addEventListener( 'scroll', () => {
			this.header.classList.toggle( 'main-header--scrolled', window.scrollY > 50 );
		}, { passive: true } );
	}

	_bindEvents() {
		// Burger toggles menu open/close.
		this.burger.addEventListener( 'click', () => {
			if ( this.isOpen ) this.close();
			else this.open();
		} );

		// Close on Escape.
		document.addEventListener( 'keydown', ( e ) => {
			if ( e.key === 'Escape' && this.isOpen ) this.close();
		} );

		// Close on nav link click (single-page transitions).
		this.overlay.querySelectorAll( 'a' ).forEach( ( link ) => {
			link.addEventListener( 'click', () => this.close() );
		} );

		// Focus trap.
		this.overlay.addEventListener( 'keydown', ( e ) => this._trapFocus( e ) );
	}

	open() {
		this.isOpen = true;
		// Record current scroll position BEFORE locking the body.
		// `body { overflow: hidden }` alone causes the page to lose its
		// scroll position on some browsers. Pinning body via `top:` with
		// `position: fixed` (handled in CSS by the .nav-open class) is
		// the standard mobile-safe pattern.
		this._scrollY = window.scrollY || window.pageYOffset || 0;
		document.body.style.setProperty( '--nav-scroll-y', `${ this._scrollY }px` );

		this.burger.setAttribute( 'aria-expanded', 'true' );
		this.burger.setAttribute( 'aria-label', 'Close navigation menu' );
		this.overlay.setAttribute( 'aria-hidden', 'false' );
		this.overlay.classList.add( 'is-open' );
		document.body.classList.add( 'nav-open' );

		// Move focus to first nav link WITHOUT scrolling — default
		// .focus() behaviour scrolls the link into view, which on
		// position:fixed overlays manifests as a small jump under the
		// menu.
		const firstLink = this.overlay.querySelector( 'a' );
		if ( firstLink ) firstLink.focus( { preventScroll: true } );
	}

	close() {
		this.isOpen = false;
		this.burger.setAttribute( 'aria-expanded', 'false' );
		this.burger.setAttribute( 'aria-label', 'Open navigation menu' );
		this.overlay.setAttribute( 'aria-hidden', 'true' );
		this.overlay.classList.remove( 'is-open' );
		document.body.classList.remove( 'nav-open' );

		// Restore the scroll position the page had before the overlay
		// opened. `body.nav-open` had pinned the page via
		// `position: fixed; top: -<scrollY>`; removing the class makes
		// the page jump to scroll 0 unless we put it back.
		if ( typeof this._scrollY === 'number' ) {
			window.scrollTo( 0, this._scrollY );
			this._scrollY = null;
		}

		this.burger.focus( { preventScroll: true } );
	}

	_trapFocus( e ) {
		if ( e.key !== 'Tab' ) return;

		const focusable = [
			...this.overlay.querySelectorAll(
				'a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex="-1"])'
			),
		].filter( ( el ) => ! el.closest( '[aria-hidden="true"]' ) );

		if ( ! focusable.length ) return;

		const first = focusable[ 0 ];
		const last  = focusable[ focusable.length - 1 ];

		if ( e.shiftKey ) {
			if ( document.activeElement === first ) {
				e.preventDefault();
				last.focus();
			}
		} else {
			if ( document.activeElement === last ) {
				e.preventDefault();
				first.focus();
			}
		}
	}
}
